import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addCartItem, clearEntireCart, removeCartItem } from "../../redux/features/Cart/CartSlice";
// Like/favourite on recommended card – commented out
// import { addFoodToFavourite } from "../../redux/features/WishList/wishlistSlice";
import AddQuantityModal from "./AddQuantityModal";
import Swal from "sweetalert2";
import fsaaiicon from '../../assets/images/fassai.png'
import menuicon from '../../assets/images/menu-icon.png'
import noImagePlaceholder from '../../assets/images/norestimage.jpg'
import MenuItem from "./MenuItem";
import { cleanupModalBackdrops } from "../../helper/useScrollToTop";
import { DEFAULT_APP_DOWNLOAD_URL } from "../../config/appDownload";

export default function RecommendedItem({
  restaurantMenu = [],
  filter,
  setFilter,
  fatchapi,
  restaurantInformation,
  isLoading = false,
  isPetpoojaIntegrated = false,
  petpoojaID = null
}) {
  const timeoutRef = useRef(null);
  const navigate = useNavigate();
  const cartdata = useSelector((state) => state.cart.cart);
  // const wishlist = useSelector((state) => state.wishlist.wishlist);
  const [modelItem, setModelItem] = useState({});
  const [filteredMenu, setFilteredMenu] = useState([]);
  const restaurantId = cartdata?.[0]?.restaurant_id_new;
  const restaurantId1 = cartdata?.[0]?.restaurant_id;
  const dispatch = useDispatch();

  // Function to open app download (ordering only on mobile app)
  const cleanupAndNavigateToCart = () => {
    cleanupModalBackdrops();
    window.open(DEFAULT_APP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
  };

  // Client-side search function for PetPooja data
  const searchMenuItems = (menuItems, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return menuItems;
    }
    
    const searchTerm = searchText.toLowerCase().trim();
    return menuItems.filter(item => 
      item.item_name?.toLowerCase().includes(searchTerm)
    );
  };

  // Update filtered menu when restaurantMenu or filter changes
  useEffect(() => {
    if (isPetpoojaIntegrated) {
      // For PetPooja data, use client-side filtering
      const filtered = searchMenuItems(restaurantMenu, filter.search_text);
      setFilteredMenu(filtered);
    } else {
      // For Application DB data, use server-side filtering
      setFilteredMenu(restaurantMenu);
    }
  }, [restaurantMenu, filter.search_text, isPetpoojaIntegrated]);

  // For Filter
  const handleChange = (e) => {
    const searchValue = e.target.value;
    setFilter((prVal) => ({ ...prVal, search_text: searchValue }));
    
    if (isPetpoojaIntegrated) {
      // For PetPooja data, update filtered menu immediately
      const filtered = searchMenuItems(restaurantMenu, searchValue);
      setFilteredMenu(filtered);
    } else {
      // For Application DB data, use server-side search with debounce
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        fatchapi();
        timeoutRef.current = null;
      }, 1000);
    }
  };

  // Handle Add To Cart – ordering only on mobile app: redirect to download
  const handleAddCart = (payload) => {
    window.open(DEFAULT_APP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
    /* Restore cart flow: use the block below and remove the window.open + return above
    const newItemRestaurantId = payload?.item?.restaurant_id_new;
    if (payload?.clearCart) {
      dispatch(clearEntireCart());
      dispatch(addCartItem(payload));
      return;
    }
    if (restaurantId && newItemRestaurantId && restaurantId !== newItemRestaurantId) {
      Swal.fire({ title: "Replace cart items?", text: "Your cart contains items from another restaurant. Do you want to clear it and add this item?", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", cancelButtonColor: "#3085d6", confirmButtonText: "Yes, clear it" })
        .then((result) => { if (result.isConfirmed) { dispatch(clearEntireCart()); dispatch(addCartItem(payload)); Swal.fire("Updated!", "Your cart has been replaced.", "success"); } });
    } else {
      dispatch(addCartItem(payload));
    }
    */
  };

  // Handle Remove To Cart
  const handleRemoveCart = (payload) => {
    dispatch(removeCartItem(payload));
  };

  const isSelfVariation = (cartItem, item) => {
    if (!cartItem.variation && (!item.variation || item.variation.length === 0)) {
      return true;
    }
    return (
      cartItem.variation?.name === item.item_name &&
      String(cartItem.variation?.price) === String(item.item_price)
    );
  };

  // Check if the default version of the item is in cart
  const isDefaultItemInCart = (item) => {
    return cartdata.some(cartItem =>
      cartItem.id === item.id &&
      (!cartItem.variation || isSelfVariation(cartItem, item))
    );
  };

  // Get quantity of the default version of the item
  const getDefaultItemQuantity = (item) => {
    const cartItem = cartdata.find(cartItem =>
      cartItem.id === item.id &&
      (!cartItem.variation || isSelfVariation(cartItem, item))
    );
    return cartItem ? cartItem.quantity : 0;
  };

  // Handle Add to favourite – commented out (like on recommended card disabled)
  // const handleAddFoodToWishlist = async (menu) => {
  //   try {
  //     dispatch(addFoodToFavourite(menu));
  //   } catch (error) {
  //   }
  // };

  // Single variation discounted price: item_price - variation_item_discount (fixed/percentage)
  const getVariationDiscountedPrice = (variation) => {
    const itemPrice = parseFloat(variation?.item_price ?? variation?.price ?? 0);
    const discount = parseFloat(variation?.variation_item_discount ?? 0);
    const discountType = (variation?.variation_item_discount_type ?? '').toLowerCase();
    if (!itemPrice) return null;
    if (discount <= 0 || (discountType !== 'percentage' && discountType !== 'fixed')) {
      return { originalPrice: itemPrice, discountedPrice: itemPrice };
    }
    let discounted = itemPrice;
    if (discountType === 'percentage') discounted = itemPrice - (itemPrice * discount / 100);
    else discounted = itemPrice - discount;
    discounted = Math.max(0, Math.round(discounted * 100) / 100);
    return { originalPrice: itemPrice, discountedPrice: discounted };
  };

  // Card display price: for items with variations use lowest variation discounted price (e.g. 145); else menu_item_discount on item_price
  const getRecommendedCardPrice = (menu) => {
    const variations = menu?.variation;
    if (Array.isArray(variations) && variations.length > 0) {
      let minOriginal = Infinity;
      let minDiscounted = Infinity;
      for (const v of variations) {
        const p = getVariationDiscountedPrice(v);
        if (p == null) continue;
        if (p.discountedPrice < minDiscounted) {
          minDiscounted = p.discountedPrice;
          minOriginal = p.originalPrice;
        }
      }
      if (minDiscounted !== Infinity && minOriginal !== Infinity) {
        return {
          originalPrice: minOriginal,
          discountedPrice: minDiscounted,
          hasDiscount: minDiscounted < minOriginal
        };
      }
    }
    return calculateDiscountedPrice(menu);
  };

  // Calculate discounted price based on menu_item_discount and menu_item_discount_type (for items without variations)
  const calculateDiscountedPrice = (menu) => {
    const itemPrice = parseFloat(menu?.item_price || menu?.base_price || 0);
    const menuItemDiscount = parseFloat(menu?.menu_item_discount || 0);
    const menuItemDiscountType = menu?.menu_item_discount_type;

    if (!menuItemDiscount || menuItemDiscount <= 0 || !menuItemDiscountType) {
      return {
        originalPrice: itemPrice,
        discountedPrice: itemPrice,
        hasDiscount: false,
        discountAmount: 0
      };
    }

    let discountedPrice = itemPrice;
    let discountAmount = 0;

    if (menuItemDiscountType.toLowerCase() === 'percentage') {
      discountAmount = (itemPrice * menuItemDiscount) / 100;
      discountedPrice = itemPrice - discountAmount;
    } else if (menuItemDiscountType.toLowerCase() === 'fixed') {
      discountAmount = menuItemDiscount;
      discountedPrice = itemPrice - discountAmount;
    }

    if (discountedPrice < 0) {
      discountedPrice = 0;
      discountAmount = itemPrice;
    }

    return {
      originalPrice: itemPrice,
      discountedPrice: Math.round(discountedPrice * 100) / 100,
      hasDiscount: true,
      discountAmount: discountAmount
    };
  };

  return (
    <>
      <section className="recommended-sec tp-space pt-0 modern-recommended">
        
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="recommended-fltr">
                <h3 className="title mb-0">Recommended</h3>
                <div className="recommended-fltr-bx">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Dish"
                    value={filter.search_text || ''}
                    onChange={handleChange}
                  />
                  <div className="dropdown">
                    <button type="button" data-bs-toggle="dropdown" aria-expanded="false" >
                      <i className="fas fa-filter" />
                    </button>
                    {/* filter dropdown content */}
                    <div className="dropdown-menu">
                      <div className="dropdown-menu-btn">
                        <h5>Short By</h5>
                        <a
                          href="#!"
                          className={
                            filter?.short_by == "all" ? "selected" : ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({
                              ...prVal,
                              short_by: "all",
                            }));
                          }}
                        >
                          All
                        </a>
                        <a
                          href="#!"
                          className={
                            filter?.short_by == "high" ? "selected" : ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({
                              ...prVal,
                              short_by: "high",
                            }));
                          }}
                        >
                           High Price
                        </a>
                        <a
                          href="#!"
                          className={
                            filter?.short_by == "low" ? "selected" : ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({
                              ...prVal,
                              short_by: "low",
                            }));
                          }}
                        >
                          Low Price
                        </a>
                      </div>
                      <div className="dropdown-menu-btn">
                        <h5>Ratting</h5>
                        <a
                          href="#!"
                          className={filter?.rating == "all" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, rating: "all" }));
                          }}
                        >
                          All
                        </a>
                        <a
                          href="#!"
                          className={filter?.rating == "5" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, rating: "5" }));
                          }}
                        >
                          <i className="fas fa-star me-1"></i>5 Only
                        </a>
                        <a
                          href="#!"
                          className={filter?.rating == "4" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, rating: "4" }));
                          }}
                        >
                          <i className="fas fa-star me-1"></i>4 & UP
                        </a>
                        <a
                          href="#!"
                          className={filter?.rating == "3" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, rating: "3" }));
                          }}
                        >
                          <i className="fas fa-star me-1"></i>3 & UP
                        </a>
                        <a
                          href="#!"
                          className={filter?.rating == "2" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, rating: "2" }));
                          }}
                        >
                          <i className="fas fa-star me-1"></i>2 & UP
                        </a>
                        <a
                          href="#!"
                          className={filter?.rating == "1" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, rating: "1" }));
                          }}
                        >
                          <i className="fas fa-star me-1 "></i>1 & UP
                        </a>
                      </div>
                      <div className="dropdown-menu-btn">
                        <h5>Type</h5>
                        <a
                          href="#!"
                          className={filter?.type == "all" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, type: "all" }));
                          }}
                        >
                          All
                        </a>
                        <a
                          href="#!"
                          className={filter?.type == "veg" ? "selected" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({ ...prVal, type: "veg" }));
                          }}
                        >
                          Veg
                        </a>
                        <a
                          href="#!"
                          className={
                            filter?.type == "non veg" ? "selected" : ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({
                              ...prVal,
                              type: "non veg",
                            }));
                          }}
                        >
                          Non-Veg
                        </a>
                      </div>
                      <div className="dropdown-menu-footer">
                        <a
                          href="#!"
                          onClick={(e) => {
                            e.preventDefault();
                            setFilter((prVal) => ({
                              ...prVal,
                              search_text: "",
                              rating: "",
                              type: "",
                              short_by: "",
                              menu_category_id: "",
                            }));
                          }}
                        >
                          Clear
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isLoading ? (
              // Show loading skeleton in the same 2x2 grid format as menu items
              <>
                <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                  <div className="recommended-card skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-rating"></div>
                      <div className="skeleton-description"></div>
                      <div className="skeleton-price"></div>
                      <div className="skeleton-button"></div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                  <div className="recommended-card skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-rating"></div>
                      <div className="skeleton-description"></div>
                      <div className="skeleton-price"></div>
                      <div className="skeleton-button"></div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                  <div className="recommended-card skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-rating"></div>
                      <div className="skeleton-description"></div>
                      <div className="skeleton-price"></div>
                      <div className="skeleton-button"></div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                  <div className="recommended-card skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-rating"></div>
                      <div className="skeleton-description"></div>
                      <div className="skeleton-price"></div>
                      <div className="skeleton-button"></div>
                    </div>
                  </div>
                </div>
              </>
            ) : filteredMenu?.length > 0 ? (
              filteredMenu?.map((menu, i) => {
                const isOutOfStock = menu.in_stock === "0";
                return (
                  <div key={menu.id} className="col-lg-6 col-md-6 col-sm-6 col-12">
                    <div className={`recommended-card ${isOutOfStock ? 'out-of-stock' : ''}`}>
                      <div className="recommended-img">
                        <img src={menu?.images?.[0] || noImagePlaceholder} alt="images" />
                        <span className="food-type">
                          <i className="fas fa-circle" />
                        </span>
                        {/* Like button on recommended card – commented out */}
                        {/* {!isOutOfStock && (
                          <button onClick={() => handleAddFoodToWishlist(menu)} className="fav-btn">
                            <i className={wishlist.some((item) => item.id === menu.id) ? "fas fa-heart text-danger" : "far fa-heart"} />
                          </button>
                        )} */}
                      </div>
                      <div className="recommended-content">
                        <h5>{menu?.item_name}</h5>
                        <p className="dtl-rating">
                          <span className="rating">
                            <i className="fas fa-star me-1" />
                            {menu?.rating}{" "}
                          </span>
                          {" "}{menu?.updated_rating} Ratings
                        </p>
                        <p className="menu-description">
                          {menu?.description || '\u00A0\u00A0'}
                        </p>
                        {/* Price and Add to cart hidden – ordering only on mobile app; code kept, not removed */}
                        {false && (
                        <div className="recommended-bttm">
                          <div className="price-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {(() => {
                              const priceInfo = getRecommendedCardPrice(menu);
                              if (priceInfo.hasDiscount) {
                                return (
                                  <>
                                    <h6 className="mb-0" style={{
                                      color: '#28a745',
                                      fontWeight: '600',
                                      fontSize: '16px',
                                      margin: 0
                                    }}>
                                      ₹{priceInfo.discountedPrice.toFixed(2)}
                                    </h6>
                                    <span style={{
                                      color: '#6c757d',
                                      textDecoration: 'line-through',
                                      fontSize: '14px',
                                      fontWeight: '400'
                                    }}>
                                      ₹{priceInfo.originalPrice.toFixed(2)}
                                    </span>
                                  </>
                                );
                              } else {
                                return (
                                  <h6 className="mb-0" style={{ margin: 0 }}>
                                    ₹{priceInfo.originalPrice.toFixed(2)}
                                  </h6>
                                );
                              }
                            })()}
                          </div>
                          {isOutOfStock ? (
                            <button className="thm-btn fill-btn" disabled style={{
                              opacity: 0.6,
                              cursor: 'not-allowed',
                              background: '#6c757d',
                              border: '1px solid #6c757d',
                              color: '#fff'
                            }}>
                              Out of Stock
                            </button>
                          ) : isDefaultItemInCart(menu) ? (
                            <div className="quantity">
                              <button
                                type="button"
                                className="minus"
                                onClick={() => handleRemoveCart({ item: menu, selectedVariation: { name: menu.item_name } })}
                              >
                                <i className="fas fa-minus" />
                              </button>
                              <input
                                className="number"
                                type="number"
                                disabled
                                value={getDefaultItemQuantity(menu)}
                              />
                              <button
                                type="button"
                                className="plus"
                                onClick={() => handleAddCart({ item: menu, selectedVariation: { name: menu.item_name, price: menu.item_price, discount_price: menu.discount_price || menu.base_price } })}
                              >
                                <i className="fas fa-plus" />
                              </button>
                            </div>
                          ) : (
                            <button
                              className="thm-btn fill-btn"
                              onClick={() => {
                                const hasVariations = menu.variation && menu.variation.length > 0;
                                let hasAddOns = false;
                                if (menu.add_ons) {
                                  if (Array.isArray(menu.add_ons) && menu.add_ons.length > 0) {
                                    hasAddOns = true;
                                  } else if (typeof menu.add_ons === 'string') {
                                    try {
                                      const parsed = JSON.parse(menu.add_ons);
                                      hasAddOns = Array.isArray(parsed) && parsed.length > 0;
                                    } catch (e) {
                                      hasAddOns = false;
                                    }
                                  }
                                }
                                const hasDesserts = menu.desserts && Array.isArray(menu.desserts) && menu.desserts.length > 0;
                                window.open(DEFAULT_APP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
                              }}
                            >
                              Add To Cart
                            </button>
                          )}
                        </div>
                        )}
                      </div>
                      {isOutOfStock && (
                        <div className="out-of-stock-overlay">
                          <span className="out-of-stock-text">Out of Stock</span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })) : (
              <h4 className="fz-4 text-center text-danger">
                <span className="fa-solid fa-skull-crossbones d-block mb-2"></span>
                No Menu Available
              </h4>
            )}
          </div>
          <div className="menu-icon">
          <button type="button" data-bs-toggle="modal" data-bs-target="#menu-item" className={filter?.menu_category_id ? "filter-active" : ""}>
            <img src={menuicon} alt="menuicon" />
            {filter?.menu_category_id && <span className="filter-badge"></span>}
          </button>
        </div>

        </div>
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              <div className="fsaai-cards">
                <div className="fsaai-img">
                  <img src={fsaaiicon} alt="" />
                </div>
                <div className="fsaai-content">
                  <h3>{restaurantInformation?.restaurant_name}</h3>
                  <p>
                    <i className="fas fa-map-marker-alt me-2" />
                    {restaurantInformation.full_address}
                  </p>
                  <h6>Fssai Licence No.  <span>{restaurantInformation?.fssai?.certificate_number}</span></h6>

                </div>
              </div>
            </div>
          </div>
        </div>
        


        {
          cartdata?.length > 0 &&
          <div className="d-flex justify-content-center">
          <div className="cart-bttm container">
            <h6>{cartdata?.length} Item Added</h6>
            <button 
              onClick={cleanupAndNavigateToCart}
              className="view-cart-btn"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#ffffff',
                textDecoration: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              View Cart<i className="fa-solid fa-chevron-right ms-1"></i>
            </button>
          </div>
          </div>
        }

      </section>
      {/* modal */}
      <AddQuantityModal
        modelItem={modelItem}
        cartdata={cartdata}
        handleAddCart={handleAddCart}
        handleRemoveCart={handleRemoveCart}
      />
      <MenuItem restaurantId={restaurantInformation.user_id || restaurantId1}
        filter={filter}
        setFilter={setFilter}
        fatchapi={fatchapi}
        isPetpoojaIntegrated={isPetpoojaIntegrated}
        petpoojaID={petpoojaID}
      />
    </>
  );
}

